import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { produtosApi, type ProdutoResumo } from '@/features/produtos/api/produtosApi';
import { clientesApi, type ClienteResumo } from '@/features/clientes/api/clientesApi';
import { FORMAS_PAGAMENTO, type FormaPagamento } from '@/features/financeiro/api/financeiroApi';
import { Field } from '@/shared/ui/Field';
import { InputMonetario } from '@/shared/ui/InputMonetario';
import { Modal } from '@/shared/ui/Modal';
import { IconTrash, IconX } from '@/shared/ui/Icon';
import { pdvApi, type CaixaResumo, type TipoDescontoItem } from '../api/pdvApi';
import { formatarDinheiro, textoDoErro } from '../formato';

type LinhaCarrinho = {
  chave: string;
  produtoId: string | null;
  codigo: string | null;
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  descontoTipo: TipoDescontoItem | null;
  descontoValor: number | null;
  /** Saldo no momento em que o item entrou — só para avisar. */
  saldo: number | null;
  inativo: boolean;
  /** Peça que ainda não está no catálogo — entra ao finalizar a venda. */
  nova: boolean;
};

type LinhaPagamento = {
  chave: string;
  formaPagamento: FormaPagamento;
  valor: number;
  valorRecebido: number | null;
};

type Props = {
  caixa: CaixaResumo;
  onVendaFinalizada: (vendaId: string) => void;
};

function arred(v: number): number {
  return Math.round(v * 100) / 100;
}

function descontoDaLinha(l: LinhaCarrinho): number {
  const bruto = arred(l.quantidade * l.valorUnitario);
  if (!l.descontoTipo || !l.descontoValor || l.descontoValor <= 0) return 0;
  if (l.descontoTipo === 'Percentual') {
    return arred((bruto * Math.min(Math.max(l.descontoValor, 0), 100)) / 100);
  }
  return Math.min(arred(l.descontoValor), bruto);
}

function subtotalDaLinha(l: LinhaCarrinho): number {
  return arred(arred(l.quantidade * l.valorUnitario) - descontoDaLinha(l));
}

function novaChave(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Tela de venda. Tudo gira em torno do campo de código: o balconista
 * escaneia ou digita, o item entra no carrinho e o foco volta para o
 * campo — sem tirar a mão do leitor.
 */
export function PdvVender({ caixa, onVendaFinalizada }: Props) {
  const [produtos, setProdutos] = useState<ProdutoResumo[]>([]);
  const [linhas, setLinhas] = useState<LinhaCarrinho[]>([]);
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [pagando, setPagando] = useState(false);
  const campoBusca = useRef<HTMLInputElement>(null);

  const recarregarCatalogo = useCallback(async () => {
    setProdutos(await produtosApi.listar());
  }, []);

  useEffect(() => {
    recarregarCatalogo().catch((e) => setErro(textoDoErro(e)));
  }, [recarregarCatalogo]);

  useEffect(() => {
    campoBusca.current?.focus();
  }, []);

  const totalBruto = useMemo(
    () => arred(linhas.reduce((acc, l) => acc + arred(l.quantidade * l.valorUnitario), 0)),
    [linhas],
  );
  const totalDesconto = useMemo(
    () => arred(linhas.reduce((acc, l) => acc + descontoDaLinha(l), 0)),
    [linhas],
  );
  const total = arred(totalBruto - totalDesconto);

  // Cobrar com item sem valor ou sem nome fecharia uma venda que ninguém
  // consegue explicar depois. O aviso aponta a linha; o botão só destrava
  // quando ela está completa.
  const incompletos = linhas.filter(
    (l) => l.valorUnitario <= 0 || (l.nova && l.descricao.trim() === ''),
  );

  /**
   * Peça do catálogo entra direto na linha, mesmo com preço zerado — o
   * operador digita o valor ali mesmo e o cadastro é completado ao
   * finalizar. Interromper a venda com um formulário para depois repetir
   * os mesmos campos na linha era trabalho em dobro no balcão.
   */
  function adicionarProduto(p: ProdutoResumo) {
    const saldo = p.estoqueQuantidade;
    setLinhas((atual) => {
      const ja = atual.find((l) => l.produtoId === p.id);
      if (ja) {
        return atual.map((l) =>
          l.chave === ja.chave ? { ...l, quantidade: l.quantidade + 1 } : l,
        );
      }
      return [
        ...atual,
        {
          chave: novaChave(),
          produtoId: p.id,
          codigo: p.codigo,
          descricao: p.nome,
          quantidade: 1,
          valorUnitario: p.precoUnitario,
          descontoTipo: null,
          descontoValor: null,
          saldo,
          inativo: !p.ativo,
          nova: false,
        },
      ];
    });
    setBusca('');
    campoBusca.current?.focus();
  }

  /**
   * Peça que não está no catálogo. Entra na lista na hora, com o que o
   * operador digitou, e vira cadastro ao finalizar a venda.
   *
   * Se o termo parece código de barras (só dígitos, 8+), ele vira o código
   * e a descrição fica em branco para o operador preencher na linha. Se
   * parece nome, vira a descrição e o código é gerado no cadastro.
   */
  function adicionarPecaNova(termo: string) {
    const pareceCodigo = /^\d{8,}$/.test(termo);
    setLinhas((atual) => [
      ...atual,
      {
        chave: novaChave(),
        produtoId: null,
        codigo: pareceCodigo ? termo : null,
        descricao: pareceCodigo ? '' : termo,
        quantidade: 1,
        valorUnitario: 0,
        descontoTipo: null,
        descontoValor: null,
        saldo: null,
        inativo: false,
        nova: true,
      },
    ]);
    setBusca('');
    campoBusca.current?.focus();
  }

  function aoConfirmarBusca(e: React.FormEvent) {
    e.preventDefault();
    const termo = busca.trim();
    if (!termo) return;
    setAviso(null);

    const porCodigo = produtos.find((p) => p.codigo.toLowerCase() === termo.toLowerCase());
    if (porCodigo) {
      adicionarProduto(porCodigo);
      return;
    }
    const porNome = produtos.filter((p) =>
      p.nome.toLowerCase().includes(termo.toLowerCase()),
    );
    if (porNome.length === 1) {
      adicionarProduto(porNome[0]);
      return;
    }
    if (porNome.length === 0) {
      adicionarPecaNova(termo);
      return;
    }
    setAviso(`${porNome.length} peças com esse nome. Escaneie o código ou refine a busca.`);
  }

  const sugestoes = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (termo.length < 2) return [];
    return produtos
      .filter(
        (p) => p.codigo.toLowerCase().includes(termo) || p.nome.toLowerCase().includes(termo),
      )
      .slice(0, 8);
  }, [busca, produtos]);

  function alterar(chave: string, mudanca: Partial<LinhaCarrinho>) {
    setLinhas((atual) => atual.map((l) => (l.chave === chave ? { ...l, ...mudanca } : l)));
  }

  function remover(chave: string) {
    setLinhas((atual) => atual.filter((l) => l.chave !== chave));
    campoBusca.current?.focus();
  }

  return (
    <div className="pdv-vender">
      <section className="pdv-vender__carrinho">
        <form className="pdv-busca" onSubmit={aoConfirmarBusca}>
          <input
            ref={campoBusca}
            className="input pdv-busca__campo"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Escaneie o código de barras ou digite o nome da peça"
            aria-label="Código de barras ou nome da peça"
            autoComplete="off"
          />
          <button className="btn btn--primary" type="submit">
            Adicionar
          </button>
        </form>

        {sugestoes.length > 0 && (
          <ul className="pdv-sugestoes">
            {sugestoes.map((p) => {
              const saldo = p.estoqueQuantidade;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    className="pdv-sugestoes__item"
                    onClick={() => adicionarProduto(p)}
                  >
                    <span className="pdv-sugestoes__codigo">{p.codigo}</span>
                    <span className="pdv-sugestoes__nome">{p.nome}</span>
                    <span className="pdv-sugestoes__valor">
                      {formatarDinheiro(p.precoUnitario)}
                    </span>
                    <span
                      className={`pdv-sugestoes__saldo${saldo <= 0 ? ' pdv-sugestoes__saldo--zero' : ''}`}
                    >
                      {saldo} em estoque
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {aviso && (
          <p className="pdv-aviso pdv-aviso--atencao" role="status">
            {aviso}
          </p>
        )}
        {erro && (
          <p className="pdv-aviso pdv-aviso--erro" role="alert">
            {erro}
          </p>
        )}

        {linhas.length === 0 ? (
          <div className="pdv-carrinho-vazio">
            <p>Nenhum item ainda.</p>
            <p className="pdv-carrinho-vazio__dica">
              Escaneie o código de barras da peça para começar.
            </p>
          </div>
        ) : (
          <ul className="pdv-itens">
            {linhas.map((l) => (
              <li key={l.chave} className="pdv-item">
                <div className="pdv-item__cabecalho">
                  <div className="pdv-item__identidade">
                    {l.nova ? (
                      <input
                        className="input pdv-item__descricao-editavel"
                        value={l.descricao}
                        placeholder="Nome da peça"
                        aria-label="Descrição da peça nova"
                        autoFocus={l.descricao === ''}
                        onChange={(e) => alterar(l.chave, { descricao: e.target.value })}
                      />
                    ) : (
                      <span className="pdv-item__descricao">{l.descricao}</span>
                    )}
                    <span className="pdv-item__codigo">
                      {l.nova && <span className="pdv-item__selo">Nova peça</span>}
                      {l.codigo ?? (l.nova ? 'código gerado no cadastro' : '')}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn--icon btn--danger"
                    aria-label={`Remover ${l.descricao}`}
                    onClick={() => remover(l.chave)}
                  >
                    <IconTrash />
                  </button>
                </div>

                {l.nova && (
                  <p className="pdv-item__aviso" role="status">
                    Entra no catálogo ao finalizar a venda. Informe a descrição e o valor.
                  </p>
                )}
                {!l.nova && l.valorUnitario <= 0 && (
                  <p className="pdv-item__aviso" role="status">
                    Esta peça está sem preço no cadastro. O valor que você digitar aqui completa o
                    cadastro dela.
                  </p>
                )}
                {l.inativo && (
                  <p className="pdv-item__aviso" role="status">
                    Peça inativa no catálogo. Pode ser vendida assim mesmo.
                  </p>
                )}
                {l.saldo != null && l.saldo - l.quantidade < 0 && (
                  <p className="pdv-item__aviso" role="status">
                    Estoque ficará negativo ({l.saldo} disponível). A venda continua normalmente.
                  </p>
                )}

                <div className="pdv-item__campos">
                  <Field rotulo="Qtd." obrigatorio>
                    {(id) => (
                      <input
                        id={id}
                        className="input pdv-num pdv-num--qtd"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        step={1}
                        value={l.quantidade}
                        onChange={(e) =>
                          alterar(l.chave, {
                            quantidade: Math.max(1, Math.floor(Number(e.target.value) || 1)),
                          })
                        }
                      />
                    )}
                  </Field>

                  <Field rotulo="Valor unitário" obrigatorio>
                    {(id) => (
                      <InputMonetario
                        id={id}
                        className="input pdv-num pdv-num--valor"
                        valor={l.valorUnitario}
                        onChange={(v) => alterar(l.chave, { valorUnitario: v })}
                      />
                    )}
                  </Field>

                  <div className="pdv-item__campo-desconto">
                    <Field rotulo="Desconto">
                      {(id) => (
                        <div className="pdv-item__desconto">
                          <select
                            id={id}
                            className="input pdv-num-select"
                            value={l.descontoTipo ?? ''}
                            onChange={(e) =>
                              alterar(l.chave, {
                                descontoTipo: (e.target.value || null) as TipoDescontoItem | null,
                                descontoValor: e.target.value ? l.descontoValor : null,
                              })
                            }
                          >
                            <option value="">Sem desconto</option>
                            <option value="Percentual">%</option>
                            <option value="Valor">R$</option>
                          </select>
                          {l.descontoTipo === 'Percentual' && (
                            <input
                              className="input pdv-num pdv-num--valor"
                              type="number"
                              inputMode="decimal"
                              min={0}
                              max={100}
                              value={l.descontoValor ?? 0}
                              aria-label="Percentual de desconto"
                              onChange={(e) =>
                                alterar(l.chave, { descontoValor: Number(e.target.value) || 0 })
                              }
                            />
                          )}
                          {l.descontoTipo === 'Valor' && (
                            <InputMonetario
                              className="input pdv-num pdv-num--valor"
                              valor={l.descontoValor ?? 0}
                              ariaLabel="Valor do desconto"
                              onChange={(v) => alterar(l.chave, { descontoValor: v })}
                            />
                          )}
                        </div>
                      )}
                    </Field>
                  </div>

                  <div className="pdv-item__subtotal">
                    <span className="pdv-item__subtotal-rotulo">Subtotal</span>
                    <strong>{formatarDinheiro(subtotalDaLinha(l))}</strong>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <aside className="pdv-vender__resumo">
        <div className="pdv-total">
          <span className="pdv-total__rotulo">Total</span>
          <strong className="pdv-total__valor">{formatarDinheiro(total)}</strong>
          {totalDesconto > 0 && (
            <span className="pdv-total__desconto">
              {formatarDinheiro(totalBruto)} − {formatarDinheiro(totalDesconto)} de desconto
            </span>
          )}
        </div>

        <dl className="pdv-total__detalhe">
          <div>
            <dt>Itens</dt>
            <dd>{linhas.reduce((acc, l) => acc + l.quantidade, 0)}</dd>
          </div>
          <div>
            <dt>Caixa de</dt>
            <dd>{caixa.colaboradorNome ?? '—'}</dd>
          </div>
        </dl>

        {incompletos.length > 0 && (
          <p className="pdv-aviso pdv-aviso--atencao" role="status">
            {incompletos.length === 1
              ? 'Falta o valor ou o nome de um item.'
              : `Faltam o valor ou o nome de ${incompletos.length} itens.`}
          </p>
        )}

        <button
          type="button"
          className="btn btn--primary pdv-botao-grande"
          disabled={linhas.length === 0 || incompletos.length > 0}
          onClick={() => setPagando(true)}
        >
          Cobrar {formatarDinheiro(total)}
        </button>

        {linhas.length > 0 && (
          <button type="button" className="btn btn--ghost" onClick={() => setLinhas([])}>
            <IconX className="btn__icon" />
            Limpar carrinho
          </button>
        )}
      </aside>

      {pagando && (
        <ModalPagamento
          caixa={caixa}
          total={total}
          linhas={linhas}
          onCancelar={() => setPagando(false)}
          onFinalizada={async (vendaId) => {
            setPagando(false);
            setLinhas([]);
            await recarregarCatalogo().catch(() => undefined);
            onVendaFinalizada(vendaId);
            campoBusca.current?.focus();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Cobrança. Aceita mais de uma forma na mesma venda — "R$50 no cartão e
 * o resto em dinheiro" é rotina, e o fechamento do caixa só bate se
 * souber quanto entrou em dinheiro especificamente.
 */
function ModalPagamento({
  caixa,
  total,
  linhas,
  onFinalizada,
  onCancelar,
}: {
  caixa: CaixaResumo;
  total: number;
  linhas: LinhaCarrinho[];
  onFinalizada: (vendaId: string) => void;
  onCancelar: () => void;
}) {
  const [pagamentos, setPagamentos] = useState<LinhaPagamento[]>([
    { chave: novaChave(), formaPagamento: 'Dinheiro', valor: total, valorRecebido: null },
  ]);
  const [fiado, setFiado] = useState(false);
  const [clientes, setClientes] = useState<ClienteResumo[]>([]);
  const [clienteId, setClienteId] = useState('');
  /** O que foi digitado. Vira cadastro novo se não casar com ninguém. */
  const [clienteNome, setClienteNome] = useState('');
  const [vencimento, setVencimento] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Só carrega a lista quando o fiado é marcado: no balcão à vista ela
  // nunca é usada, e a tela de clientes já carrega tudo em memória.
  useEffect(() => {
    if (!fiado || clientes.length > 0) return;
    void clientesApi
      .listar()
      .then(setClientes)
      .catch((e) => setErro(textoDoErro(e)));
  }, [fiado, clientes.length]);
  // Gerado uma vez e reusado em toda tentativa: é a chave de
  // idempotência. Retentativa depois de um timeout não cobra duas vezes.
  const idVenda = useRef(crypto.randomUUID());

  const somaPagamentos = arred(pagamentos.reduce((acc, p) => acc + p.valor, 0));
  const faltando = arred(total - somaPagamentos);
  // No fiado o que entra é uma entrada: pode ser zero, nunca mais que o
  // total. O resto vira saldo a receber no financeiro.
  const pagamentoValido = fiado ? somaPagamentos <= total + 0.005 : Math.abs(faltando) < 0.01;
  const troco = arred(
    pagamentos.reduce(
      (acc, p) =>
        acc +
        (p.formaPagamento === 'Dinheiro' && p.valorRecebido != null
          ? Math.max(0, p.valorRecebido - p.valor)
          : 0),
      0,
    ),
  );

  // Só sugere enquanto não há escolha firmada, e some quando o nome
  // digitado já é exatamente o de alguém.
  const clienteSugestoes = (() => {
    const termo = clienteNome.trim().toLowerCase();
    if (!fiado || termo.length < 2 || clienteId) return [];
    return clientes.filter((c) => c.nome.toLowerCase().includes(termo)).slice(0, 5);
  })();

  function alterarPagamento(chave: string, mudanca: Partial<LinhaPagamento>) {
    setPagamentos((atual) => atual.map((p) => (p.chave === chave ? { ...p, ...mudanca } : p)));
  }

  async function finalizar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const resposta = await pdvApi.criarVenda({
        id: idVenda.current,
        caixaId: caixa.id,
        clienteId: fiado && clienteId ? clienteId : null,
        // Sem id e com nome digitado, o backend cadastra o cliente.
        clienteNovo: fiado && !clienteId && clienteNome.trim() ? { nome: clienteNome.trim() } : null,
        fiado,
        vencimento: fiado ? vencimento : null,
        itens: linhas.map((l) => ({
          produtoId: l.produtoId,
          codigo: l.codigo,
          descricao: l.descricao,
          quantidade: l.quantidade,
          valorUnitario: l.valorUnitario,
          descontoTipo: l.descontoTipo,
          descontoValor: l.descontoValor,
        })),
        pagamentos: pagamentos.map((p) => ({
          formaPagamento: p.formaPagamento,
          valor: p.valor,
          valorRecebido: p.formaPagamento === 'Dinheiro' ? p.valorRecebido : null,
        })),
      });
      onFinalizada(resposta.id);
    } catch (err) {
      setErro(textoDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal aberto titulo={`Cobrar ${formatarDinheiro(total)}`} onFechar={onCancelar} tamanho="md">
      <form className="pdv-form-modal" onSubmit={finalizar}>
        <label className="pdv-fiado-toggle">
          <input
            type="checkbox"
            checked={fiado}
            onChange={(e) => {
              setFiado(e.target.checked);
              // À vista o padrão é cobrar tudo; no fiado, nada.
              setPagamentos(
                e.target.checked
                  ? []
                  : [
                      {
                        chave: novaChave(),
                        formaPagamento: 'Dinheiro',
                        valor: total,
                        valorRecebido: null,
                      },
                    ],
              );
            }}
          />
          <span>
            <strong>Fiado</strong> — o cliente leva agora e paga depois
          </span>
        </label>

        {fiado && (
          <>
            <Field
              rotulo="Cliente"
              obrigatorio
              dica="Digite o nome. Se ainda não existir, é cadastrado ao finalizar a venda."
            >
              {(id) => (
                <div className="pdv-cliente">
                  <input
                    id={id}
                    className="input"
                    value={clienteNome}
                    autoComplete="off"
                    placeholder="Nome do cliente"
                    onChange={(e) => {
                      setClienteNome(e.target.value);
                      // Digitar depois de escolher desfaz a escolha: o que
                      // vale é o que está escrito no campo.
                      const exato = clientes.find(
                        (c) => c.nome.toLowerCase() === e.target.value.trim().toLowerCase(),
                      );
                      setClienteId(exato?.id ?? '');
                    }}
                    required
                  />

                  {clienteSugestoes.length > 0 && (
                    <ul className="pdv-cliente__sugestoes">
                      {clienteSugestoes.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            className="pdv-cliente__item"
                            onClick={() => {
                              setClienteId(c.id);
                              setClienteNome(c.nome);
                            }}
                          >
                            {c.nome}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {clienteNome.trim() !== '' && !clienteId && (
                    <span className="pdv-cliente__novo">
                      Cliente novo — será cadastrado como <strong>{clienteNome.trim()}</strong>.
                    </span>
                  )}
                </div>
              )}
            </Field>

            <Field rotulo="Vencimento" obrigatorio>
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="date"
                  value={vencimento}
                  onChange={(e) => setVencimento(e.target.value)}
                  required
                />
              )}
            </Field>

            <p className="pdv-cartao__ajuda">
              A venda vira uma conta a receber no financeiro. Se o cliente deixar uma entrada,
              informe abaixo — ela já entra como baixa.
            </p>

            {pagamentos.length === 0 && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() =>
                  setPagamentos([
                    {
                      chave: novaChave(),
                      formaPagamento: 'Dinheiro',
                      valor: 0,
                      valorRecebido: null,
                    },
                  ])
                }
              >
                Registrar entrada
              </button>
            )}
          </>
        )}

        {pagamentos.map((p, i) => (
          <div key={p.chave} className="pdv-pagamento">
            <Field
              rotulo={
                fiado
                  ? 'Entrada em'
                  : pagamentos.length > 1
                    ? `Forma ${i + 1}`
                    : 'Forma de pagamento'
              }
            >
              {(id) => (
                <select
                  id={id}
                  className="input pdv-num-select"
                  value={p.formaPagamento}
                  onChange={(e) =>
                    alterarPagamento(p.chave, {
                      formaPagamento: e.target.value as FormaPagamento,
                      valorRecebido: null,
                    })
                  }
                >
                  {FORMAS_PAGAMENTO.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field rotulo="Valor">
              {(id) => (
                <InputMonetario
                  id={id}
                  className="input pdv-num pdv-num--valor"
                  valor={p.valor}
                  onChange={(v) => alterarPagamento(p.chave, { valor: v })}
                />
              )}
            </Field>

            {p.formaPagamento === 'Dinheiro' && (
              <Field rotulo="Recebido" dica="Deixe em branco se recebeu o valor exato.">
                {(id) => (
                  <InputMonetario
                    id={id}
                    className="input pdv-num pdv-num--valor"
                    valor={p.valorRecebido ?? 0}
                    onChange={(v) => alterarPagamento(p.chave, { valorRecebido: v > 0 ? v : null })}
                  />
                )}
              </Field>
            )}

            {(pagamentos.length > 1 || fiado) && (
              <button
                type="button"
                className="btn btn--icon btn--danger"
                aria-label="Remover forma de pagamento"
                onClick={() => setPagamentos((a) => a.filter((x) => x.chave !== p.chave))}
              >
                <IconTrash />
              </button>
            )}
          </div>
        ))}

        {Math.abs(faltando) >= 0.01 && !fiado && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() =>
              setPagamentos((a) => [
                ...a,
                {
                  chave: novaChave(),
                  formaPagamento: 'Pix',
                  valor: Math.max(0, faltando),
                  valorRecebido: null,
                },
              ])
            }
          >
            Dividir em outra forma ({formatarDinheiro(Math.abs(faltando))} restante)
          </button>
        )}

        <dl className="pdv-total__detalhe">
          <div>
            <dt>Total da venda</dt>
            <dd>{formatarDinheiro(total)}</dd>
          </div>
          <div>
            <dt>Somado nas formas</dt>
            <dd>{formatarDinheiro(somaPagamentos)}</dd>
          </div>
          {troco > 0 && (
            <div className="pdv-troco">
              <dt>Troco</dt>
              <dd>{formatarDinheiro(troco)}</dd>
            </div>
          )}
          {fiado && (
            <div className="pdv-fiado-saldo">
              <dt>Fica devendo</dt>
              <dd>{formatarDinheiro(Math.max(0, faltando))}</dd>
            </div>
          )}
        </dl>

        {!fiado && Math.abs(faltando) >= 0.01 && (
          <p className="pdv-aviso pdv-aviso--atencao" role="status">
            {faltando > 0
              ? `Faltam ${formatarDinheiro(faltando)} para fechar a venda.`
              : `Os pagamentos passam ${formatarDinheiro(-faltando)} do total. Ajuste os valores — o excedente em dinheiro vira troco no campo "Recebido".`}
          </p>
        )}
        {fiado && faltando < -0.005 && (
          <p className="pdv-aviso pdv-aviso--atencao" role="status">
            A entrada passa {formatarDinheiro(-faltando)} do valor da venda.
          </p>
        )}

        {erro && (
          <p className="pdv-aviso pdv-aviso--erro" role="alert">
            {erro}
          </p>
        )}

        <div className="pdv-form-modal__acoes">
          <button type="button" className="btn btn--ghost" onClick={onCancelar}>
            Voltar
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={salvando || !pagamentoValido || (fiado && clienteNome.trim() === '')}
          >
            {salvando ? 'Finalizando…' : fiado ? 'Registrar no fiado' : 'Finalizar venda'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
